import { useMemo } from "react";
import { QUERY_CACHE_BLOCK_TIME, REQUEST_ID } from "@sablier/v2-constants";
import { framework } from "@sablier/v2-contracts";
import { BigNumber, _ } from "@sablier/v2-mixins";
import { useQuery } from "@tanstack/react-query";
import type { Output } from "@sablier/v2-contracts";
import type { IStream } from "@sablier/v2-models";
import type { IValue } from "@sablier/v2-types";

interface Props {
  stream?: IStream;
}

export async function requestWithdrawable({ stream }: Props): Promise<IValue> {
  try {
    if (_.isNil(stream) || _.isNilOrEmptyString(stream.tokenId)) {
      throw new Error("Missing stream.");
    }

    const query = framework.contextualize(
      stream.contract,
      stream.chainId,
      stream.category,
      "withdrawableAmountOf",
      [_.toBigInt(stream.tokenId)],
    );

    const previews = await framework.preview({ queries: [query] });
    const results = await framework.read(previews);

    const output = results[0].result as Output<
      typeof stream.category,
      "withdrawableAmountOf"
    >;

    const withdrawable = _.toValue({
      decimals: stream.token.decimals,
      raw: new BigNumber(output.toString()),
    });

    return withdrawable;
  } catch (error) {
    throw new Error("Failed to extract withdrawable amount.");
  }
}

export default function useRequestWithdrawable({ stream }: Props) {
  const {
    data: value,
    error,
    isLoading,
  } = useQuery<IValue | undefined>({
    queryKey: [...REQUEST_ID.streamItemWithdrawable, { unique: stream?.id }],
    queryFn: async () => requestWithdrawable({ stream }),
    staleTime: QUERY_CACHE_BLOCK_TIME,
    cacheTime: QUERY_CACHE_BLOCK_TIME,
    retry: false,
    enabled: !_.isNilOrEmptyString(stream?.tokenId),
  });

  return useMemo(
    () => ({
      error,
      isLoading,
      value,
      request: requestWithdrawable,
    }),
    [error, isLoading, value],
  );
}
